import { useMemo } from "react";

import { useScenarioTemplatesQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

// Define the template type directly based on our needs
interface Template {
  id: string;
  name: string;
  content: string;
}

// Templates grouped by category
type TemplatesByCategory = Record<string, Template[]>;
type TemplatesById = Record<string, Template>;
export function useScenarioTemplates(): {
  templatesByCategory: TemplatesByCategory;
  templatesById: TemplatesById;
} {
  const { data, loading } = useScenarioTemplatesQuery();

  const templatesByCategory = useMemo(() => {
    const result: TemplatesByCategory = {};

    if (!data?.scenarioTemplates || loading) {
      return {};
    }

    const templates = ensureNonNullArray(data.scenarioTemplates);

    // Group templates by category
    templates.forEach((template) => {
      if (!template.active) {
        return;
      }

      const category = template.category ?? "Uncategorized";
      result[category] = result[category] || [];

      result[category].push({
        id: template.id!,
        name: template.name!,
        content: template.content || "",
      });
    });

    return result;
  }, [data, loading]);

  const templatesById = useMemo(() => {
    const result: Record<string, Template> = {};

    if (!data?.scenarioTemplates || loading) {
      return {};
    }

    const templates = ensureNonNullArray(data.scenarioTemplates);

    templates.forEach((template) => {
      if (!template.active) {
        return;
      }

      result[template.id!] = {
        id: template.id!,
        name: template.name!,
        content: template.content || "",
      };
    });

    return result;
  }, [data, loading]);

  return { templatesByCategory, templatesById };
}
